import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import { Box, CircularProgress } from '@mui/material';

import type { AssignmentFormData } from './TagAssignments/TagAssignments';
import TagForm, { type FormTag, type TagDraftSchedule } from './TagForm';
import { TAG_TYPE_AWARENESS_BUNDLE } from './constants';
import { useGetTag, useUpdateTag } from './hooks';
import { useCreateTagAssignments } from './hooks/useTagAssignments';
import { useTagAssignmentsForForm } from './hooks/useTagAssignmentsForForm';
import { useCreateTagSchedule, useUpdateTagSchedule } from './hooks/useTagSchedules';

interface Props {
  tagId: string;
  onBack: () => void;
  onViewContent: (tagName: string) => void;
}

function createDraftSchedule(schedule: {
  id?: string;
  tagId?: string;
  brands: Array<string>;
  regions: Array<string>;
  activeStartDate: string;
  activeEndDate: string;
  travelStartDate?: string;
  travelEndDate?: string;
}): TagDraftSchedule {
  return {
    localId: schedule.id ?? `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    id: schedule.id,
    tagId: schedule.tagId,
    brands: schedule.brands,
    regions: schedule.regions,
    activeStartDate: schedule.activeStartDate,
    activeEndDate: schedule.activeEndDate,
    travelStartDate: schedule.travelStartDate,
    travelEndDate: schedule.travelEndDate,
  };
}

function serializeSchedule(schedule: TagDraftSchedule) {
  return JSON.stringify({
    brands: schedule.brands,
    regions: schedule.regions,
    activeStartDate: schedule.activeStartDate,
    activeEndDate: schedule.activeEndDate,
    travelStartDate: schedule.travelStartDate ?? null,
    travelEndDate: schedule.travelEndDate ?? null,
  });
}

export default function TagEdit({ tagId, onBack, onViewContent }: Props) {
  const [assignments, setAssignments] = useState<Array<AssignmentFormData>>([]);
  const initialAssignmentsRef = useRef<Array<AssignmentFormData>>([]);
  const initialSchedulesRef = useRef<Array<TagDraftSchedule>>([]);

  const { enqueueSnackbar } = useSnackbar();
  const { data: tag = null, isLoading: isLoadingTag = false } = useGetTag(tagId);
  const updateTag = useUpdateTag();
  const createTagAssignments = useCreateTagAssignments();
  const createTagSchedule = useCreateTagSchedule();
  const updateTagSchedule = useUpdateTagSchedule();

  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const isLoadingAssignments = tagAssignmentsData.isLoading;

  const form = useForm<FormTag>({
    defaultValues: {
      name: '',
      tagType: TAG_TYPE_AWARENESS_BUNDLE,
      schedules: [],
    },
  });
  const { control, handleSubmit, reset, formState, getValues } = form;

  useEffect(() => {
    if (tag && savedAssignments) {
      const formSchedules = (tag.schedules || []).map((schedule) =>
        createDraftSchedule({
          id: schedule.id,
          tagId: schedule.tagId,
          brands: schedule.brands,
          regions: schedule.regions,
          activeStartDate: schedule.activeStartDate,
          activeEndDate: schedule.activeEndDate,
          travelStartDate: schedule.travelStartDate,
          travelEndDate: schedule.travelEndDate,
        }),
      );
      reset({
        name: tag.name,
        tagType: tag.tagType ?? TAG_TYPE_AWARENESS_BUNDLE,
        schedules: formSchedules,
      });
      setAssignments(savedAssignments);
      initialAssignmentsRef.current = savedAssignments;
      initialSchedulesRef.current = formSchedules;
    }
  }, [tag, savedAssignments, reset]);

  const assignmentsToAdd = useMemo(() => {
    const initial = initialAssignmentsRef.current;
    const current = assignments;

    const initialMap = new Map(initial.map((a) => [`${a.entityType}-${a.entityId}`, a]));

    return current.filter((a) => !initialMap.has(`${a.entityType}-${a.entityId}`));
  }, [assignments]);

  const handleSave = async () => {
    try {
      const currentSchedules = getValues('schedules') || [];
      const initialSchedules = initialSchedulesRef.current;
      const initialSchedulesById = new Map(
        initialSchedules.flatMap((schedule) => (schedule.id ? [[schedule.id, schedule] as const] : [])),
      );
      const schedulesToCreate = currentSchedules.filter((schedule) => !schedule.id);
      const schedulesToUpdate = currentSchedules.filter((schedule) => {
        if (!schedule.id) {
          return false;
        }

        const initialSchedule = initialSchedulesById.get(schedule.id);

        if (!initialSchedule) {
          return false;
        }

        return serializeSchedule(initialSchedule) !== serializeSchedule(schedule);
      });
      const currentTagType = getValues('tagType');

      if ((tag?.tagType ?? TAG_TYPE_AWARENESS_BUNDLE) !== currentTagType) {
        await updateTag.mutateAsync({
          id: tagId,
          data: {
            tagType: currentTagType,
          },
        });
      }

      if (assignmentsToAdd.length > 0) {
        await createTagAssignments.mutateAsync({
          tagId,
          assignments: assignmentsToAdd.map((assignment) => ({
            entityType: assignment.entityType,
            entityId: assignment.entityId,
          })),
        });
      }

      for (const schedule of schedulesToCreate) {
        await createTagSchedule.mutateAsync({
          tagId,
          brands: schedule.brands,
          regions: schedule.regions,
          activeStartDate: schedule.activeStartDate,
          activeEndDate: schedule.activeEndDate,
          travelStartDate: schedule.travelStartDate || undefined,
          travelEndDate: schedule.travelEndDate || undefined,
        });
      }

      for (const schedule of schedulesToUpdate) {
        if (!schedule.id) {
          continue;
        }

        await updateTagSchedule.mutateAsync({
          id: schedule.id,
          data: {
            brands: schedule.brands,
            regions: schedule.regions,
            activeStartDate: schedule.activeStartDate,
            activeEndDate: schedule.activeEndDate,
            travelStartDate: schedule.travelStartDate || undefined,
            travelEndDate: schedule.travelEndDate || undefined,
          },
        });
      }

      enqueueSnackbar('Tag saved successfully', { variant: 'success' });
      onBack();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      enqueueSnackbar(`Error saving tag: ${message}`, { variant: 'error' });
    }
  };

  if (isLoadingTag || isLoadingAssignments) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...form}>
      <TagForm
        mode="edit"
        tag={tag}
        control={control}
        formState={formState}
        assignments={assignments}
        onAssignmentsChange={setAssignments}
        initialAssignments={initialAssignmentsRef.current}
        isSaving={
          updateTag.isPending ||
          createTagAssignments.isPending ||
          createTagSchedule.isPending ||
          updateTagSchedule.isPending
        }
        onBack={onBack}
        onSave={handleSubmit(handleSave, (errors) => {
          enqueueSnackbar(errors['schedules']?.message || errors['tagType']?.message || 'Unknown error', {
            variant: 'error',
          });
        })}
        onViewContent={tag?.name ? () => onViewContent(tag.name) : undefined}
      />
    </FormProvider>
  );
}
